"use client";

import { useState, useCallback, useRef } from "react";
import ReactCrop, { type Crop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button, Modal, Box as MantineBox, Image as MantineImage } from "@mantine/core";
import { useMediaQuery } from "react-responsive";
import { getCroppedImage } from "@/lib/client/cropImage";
import styles from "./styles.module.scss";

interface Props {
  imageUrl: string;
  onConfirm: (croppedBase64: string, croppedPixels: PixelCrop) => void;
  onCancel: () => void;
  /** Pre-populate the crop selection (used when re-cropping a previous result) */
  initialCropArea?: PixelCrop;
  /** Confirm button label — callers using this outside of image search (e.g. appraise) should describe what confirming actually does. */
  confirmLabel?: string;
}

const INSET_PERCENT = 5;

function toProxiedUrl(url: string): string {
  if (url.startsWith("data:") || url.startsWith("/")) return url;
  return `/api/image-proxy?url=${encodeURIComponent(url)}`;
}

export function ImageCropOverlay({
  imageUrl,
  onConfirm,
  onCancel,
  initialCropArea,
  confirmLabel = "SEARCH THIS CROP",
}: Props) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(
    initialCropArea ?? undefined
  );
  const [confirming, setConfirming] = useState(false);

  const isMobile = useMediaQuery({ query: "(max-width: 1250px)" });

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight, width, height } = e.currentTarget;

      if (initialCropArea) {
        // Convert pixel crop (in natural image coords) to percentage crop for display
        const scaleX = width / naturalWidth;
        const scaleY = height / naturalHeight;
        setCrop({
          unit: "px",
          x: initialCropArea.x * scaleX,
          y: initialCropArea.y * scaleY,
          width: initialCropArea.width * scaleX,
          height: initialCropArea.height * scaleY,
        });
        setCompletedCrop(initialCropArea);
      } else {
        // Default: 50px inset as percentage of display size
        const insetPx = Math.min(width, height) * (INSET_PERCENT / 100);
        setCrop({
          unit: "px",
          x: insetPx,
          y: insetPx,
          width: width - insetPx * 2,
          height: height - insetPx * 2,
        });
        // Set initial completedCrop in natural pixels
        const scaleX = naturalWidth / width;
        const scaleY = naturalHeight / height;
        setCompletedCrop({
          unit: "px",
          x: Math.round(insetPx * scaleX),
          y: Math.round(insetPx * scaleY),
          width: Math.round((width - insetPx * 2) * scaleX),
          height: Math.round((height - insetPx * 2) * scaleY),
        });
      }
    },
    [initialCropArea]
  );

  const handleConfirm = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return;
    setConfirming(true);
    try {
      const cropped = await getCroppedImage(imageUrl, completedCrop);
      onConfirm(cropped, completedCrop);
    } catch {
      // CORS-blocked external URLs — send URL directly without crop
      onConfirm(imageUrl, completedCrop);
    } finally {
      setConfirming(false);
    }
  }, [completedCrop, imageUrl, onConfirm]);

  const content = (
    <MantineBox className={styles.cropWrapper}>
      <MantineBox className={styles.cropArea}>
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => {
            if (!imgRef.current || !c.width || !c.height) return;
            const img = imgRef.current;
            const scaleX = img.naturalWidth / img.width;
            const scaleY = img.naturalHeight / img.height;
            setCompletedCrop({
              unit: "px",
              x: Math.round(c.x * scaleX),
              y: Math.round(c.y * scaleY),
              width: Math.round(c.width * scaleX),
              height: Math.round(c.height * scaleY),
            });
          }}
          keepSelection
        >
          <MantineImage
            ref={imgRef}
            src={toProxiedUrl(imageUrl)}
            alt="Crop preview"
            className={styles.cropImg}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      </MantineBox>
      <MantineBox className={styles.actions}>
        <Button variant="outline" size="xs" radius={0} onClick={onCancel} fw={700}>
          CANCEL
        </Button>
        <Button
          size="xs"
          radius={0}
          onClick={handleConfirm}
          loading={confirming}
          fw={700}
          color="dark"
        >
          {confirmLabel}
        </Button>
      </MantineBox>
    </MantineBox>
  );

  if (isMobile) {
    return <MantineBox className={styles.inlinePanel}>{content}</MantineBox>;
  }

  return (
    <Modal opened onClose={onCancel} title="Adjust crop" size="md" centered radius={4}>
      {content}
    </Modal>
  );
}
